import Dexie, { type DBCoreMutateRequest, type Table } from 'dexie';
import type { AuditEvent, CountEntry, Feedback, Item, Movement, Night, Person, PlanEvent, Settings } from '@/domain/types';
import { isSyncedTable } from '@/sync/protocol';

/** A record changed on this device that the other devices haven't got yet. */
export interface OutboxEntry {
  /** `${table}|${id}` */
  key: string;
  tbl: string;
  id: string;
  at: number;
}

/** The share's copy of a record as last seen (the starting point for merging). */
export interface Shadow {
  key: string;
  /** The share's version. */
  v: number;
  data: Record<string, unknown> | null;
  /** This device's version/time of the record when it matched the share. */
  lv: number | null;
  lu: string | null;
}

/**
 * This device's storage (the browser's built-in IndexedDB: free, on the tablet, works with
 * no internet). With sharing on, changes also go to the other devices through the share;
 * the weekly Excel files stay the record kept off the devices.
 */
export class LastCallDB extends Dexie {
  meta!: Table<{ key: string; value: unknown }, string>;
  settings!: Table<Settings, string>;
  people!: Table<Person, string>;
  items!: Table<Item, string>;
  nights!: Table<Night, string>;
  counts!: Table<CountEntry, string>;
  moves!: Table<Movement, string>;
  events!: Table<PlanEvent, string>;
  audit!: Table<AuditEvent, string>;
  feedback!: Table<Feedback, string>;
  outbox!: Table<OutboxEntry, string>;
  shadows!: Table<Shadow, string>;
  /** Set by the sharing engine while sharing is on for this device. */
  syncOn = false;
  /** Called after a shared table changes (the engine sends it soon after). */
  onLocalChange: (() => void) | null = null;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      meta: 'key',
      settings: 'id',
      people: 'id',
      items: 'id, kind',
      nights: 'id, status',
      // recountRequestedAt is only indexed when set (IndexedDB skips null), so "waiting recounts" is a cheap lookup.
      counts: 'id, date, itemId, recountRequestedAt',
      moves: 'id, date, itemId',
      events: 'id',
      audit: 'id, at, entityId',
    });
    this.version(2).stores({ outbox: 'key, at', shadows: 'key' });
    this.version(3).stores({ feedback: 'id, at, status' });

    // Every write to a shared table made through the normal write path (whose transactions
    // include the outbox) is noted in the outbox in the same transaction — so a change can't
    // be saved without also being queued for the other devices. Changes that arrive from the
    // share are written without the outbox in the transaction, so they aren't sent back.
    this.use({
      stack: 'dbcore',
      name: 'lastcall-outbox',
      create: (down) => ({
        ...down,
        table: (name) => {
          const table = down.table(name);
          if (!isSyncedTable(name)) return table;
          return {
            ...table,
            mutate: async (req: DBCoreMutateRequest) => {
              const res = await table.mutate(req);
              if (!this.syncOn) return res;
              const idb = req.trans as unknown as IDBTransaction;
              if (!idb?.objectStoreNames?.contains?.('outbox')) return res;
              let ids: unknown[] = [];
              if (req.type === 'add' || req.type === 'put') ids = req.keys ?? req.values.map((v) => (v as { id?: unknown }).id);
              else if (req.type === 'delete') ids = req.keys;
              const at = Date.now();
              const values = ids.filter((id): id is string => typeof id === 'string').map((id) => ({ key: `${name}|${id}`, tbl: name, id, at }));
              if (values.length) {
                await down.table('outbox').mutate({ type: 'put', trans: req.trans, values });
                const notify = this.onLocalChange;
                if (notify) setTimeout(notify, 0);
              }
              return res;
            },
          };
        },
      }),
    });
  }
}

/** Tables holding the venue's records (everything except the history log and device notes). */
export const DATA_TABLES = ['settings', 'people', 'items', 'nights', 'counts', 'moves', 'events'] as const;
